'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { AlertTriangle, RotateCcw, Home, Phone, LifeBuoy } from 'lucide-react';

export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('App Runtime Error:', error);
  }, [error]);

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-4 sm:px-8 py-16 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
      <div className="max-w-xl w-full text-center space-y-8">
        {/* Warning Icon Graphic */}
        <div className="relative inline-block">
          <div className="w-24 h-24 rounded-3xl bg-amber-500/10 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 flex items-center justify-center mx-auto shadow-xl">
            <AlertTriangle className="w-12 h-12 text-amber-500 animate-bounce" />
          </div>
          <div className="absolute inset-0 bg-amber-500/10 blur-2xl -z-10 rounded-full" />
        </div>

        {/* Text description */}
        <div className="space-y-3">
          <span className="px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 text-xs font-bold border border-amber-200 dark:border-amber-800">
            Kodi i Gabimit: 500 / Runtime Error
          </span>
          <h1 className="text-2xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            Ndodhi një ndërprerje e papritur në sistem
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto leading-relaxed">
            Inxhinierët tanë të sistemit janë njoftuar automatikisht. Ju lutem provoni ta ngarkoni sërish këtë faqe.
          </p>

          {error.digest && (
            <div className="text-[11px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-900 px-3 py-1.5 rounded-lg inline-block border border-slate-200 dark:border-slate-800">
              Digest ID: {error.digest}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <button
            onClick={() => reset()}
            className="px-6 py-3 rounded-2xl bg-sky-600 hover:bg-sky-500 text-white font-extrabold text-xs sm:text-sm flex items-center gap-2 shadow-lg shadow-sky-600/25 transition-all hover:scale-105"
          >
            <RotateCcw className="w-4 h-4" />
            Provo Përsëri
          </button>

          <Link
            href="/"
            className="px-6 py-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-sky-300 dark:hover:border-sky-700 text-slate-800 dark:text-slate-200 font-bold text-xs sm:text-sm flex items-center gap-2 shadow-sm transition-all"
          >
            <Home className="w-4 h-4 text-sky-600 dark:text-sky-400" />
            Kthehu në Ballinë
          </Link>

          <a
            href="https://wa.me/355693673867"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm flex items-center gap-2 transition-all shadow-md shadow-emerald-600/20"
          >
            <Phone className="w-4 h-4" />
            Raporto në WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
